// @ts-nocheck
import React, { useState, useEffect, useMemo, ChangeEvent } from 'react';
import DateRangePicker, { EventHandler } from 'react-bootstrap-daterangepicker';
import { dynamicDateRanges } from '../../../util/dateRanges';
import { DateForFilter } from '../../../util/formatter';
import icons from '../../../constants/icons';
import Button from '../../common/Button';
import { useTranslation } from 'react-i18next';
import FormSelect from '../../common/FormSelect';
import { useLocalStorage } from '../../../hooks/useLocalStorage';

const months = [
  { value: '1', label: 'Jan' },
  { value: '2', label: 'Feb' },
  { value: '3', label: 'Mar' },
  { value: '4', label: 'Apr' },
  { value: '5', label: 'May' },
  { value: '6', label: 'Jun' },
  { value: '7', label: 'Jul' },
  { value: '8', label: 'Aug' },
  { value: '9', label: 'Sep' },
  { value: '10', label: 'Oct' },
  { value: '11', label: 'Nov' },
  { value: '12', label: 'Dec' },
];

function DateRange({
  range,
  onChange,
  className,
  useDynamicRanges = false,
  setStorageRange,
}: {
  range: { from: Date | string; to: Date | string; dynamic?: string };
  onChange: (range: { from: Date; to: Date; dynamic?: string }) => void;
  className?: string;
  useDynamicRanges?: boolean;
  setStorageRange?: (range: string) => void;
}) {
  const { t } = useTranslation();

  const getInitialPickerType = () => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('dateRangePickerType') || 'bootstrap';
    }

    return 'bootstrap';
  };

  const [pickerType, setPickerType] = useLocalStorage<'bootstrap' | 'dropdown'>(
    getInitialPickerType(),
    'bootstrap'
  );

  const initialFromDate = range.from instanceof Date ? range.from : new Date();
  const initialToDate = range.to instanceof Date ? range.to : new Date();
  const [fromMonth, setFromMonth] = useState(
    (initialFromDate.getMonth() + 1).toString()
  );
  const [fromYear, setFromYear] = useState(
    initialFromDate.getFullYear().toString()
  );
  const [toMonth, setToMonth] = useState(
    (initialToDate.getMonth() + 1).toString()
  );
  const [toYear, setToYear] = useState(initialToDate.getFullYear().toString());

  const currentYear = new Date().getFullYear();
  const prevRangeRef = React.useRef<{ from: Date | null; to: Date | null }>({
    from: null,
    to: null,
  });

  const years = useMemo(() => {
    const yearArray = [];
    for (let year = 2010; year <= currentYear + 1; year++) {
      yearArray.push({ value: year.toString(), label: year.toString() });
    }
    return yearArray;
  }, [currentYear]);

  const dynamicRanges = useMemo(() => {
    const ranges: Record<string, [Date, Date]> = {};
    if (useDynamicRanges) {
      (
        Object.keys(dynamicDateRanges) as Array<keyof typeof dynamicDateRanges>
      ).forEach((rangeKey) => {
        const dynamicRange = dynamicDateRanges[rangeKey];
        ranges[dynamicRange.label] = [
          dynamicRange.calculateFrom(),
          dynamicRange.calculateTo(),
        ];
      });
    }
    return ranges;
  }, [useDynamicRanges, dynamicDateRanges]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('dateRangePickerType', pickerType);
    }
  }, [pickerType]);

  useEffect(() => {
    if (fromYear && fromMonth && toYear && toMonth) {
      const fromYearInt = parseInt(fromYear);
      const fromMonthInt = parseInt(fromMonth);
      const toYearInt = parseInt(toYear);
      const toMonthInt = parseInt(toMonth);

      if (toYearInt < fromYearInt) {
        setToYear(fromYear);
        setToMonth(fromMonth);
        return;
      }

      if (toYearInt === fromYearInt && toMonthInt < fromMonthInt) {
        setToMonth(fromMonth);
        return;
      }
    }
  }, [fromYear, fromMonth, toYear, toMonth]);

  useEffect(() => {
    if (pickerType === 'bootstrap') return;
    if (!(fromMonth && fromYear && toMonth && toYear)) return;

    const from = new Date(parseInt(fromYear), parseInt(fromMonth) - 1, 1);
    from.setHours(0, 0, 0, 0);

    const to = new Date(parseInt(toYear), parseInt(toMonth), 0);
    to.setHours(23, 59, 59, 999);

    const prevFrom = prevRangeRef.current.from;
    const prevTo = prevRangeRef.current.to;
    const sameFrom =
      prevFrom && prevFrom.getTime && prevFrom.getTime() === from.getTime();
    const sameTo =
      prevTo && prevTo.getTime && prevTo.getTime() === to.getTime();
    if (sameFrom && sameTo) {
      return;
    }

    prevRangeRef.current = { from, to };
    if (setStorageRange) {
      setStorageRange('Custom Range');
    }
    onChange({ from, to, dynamic: 'Custom Range' });
  }, [
    fromMonth,
    fromYear,
    toMonth,
    toYear,
    pickerType,
    setStorageRange,
    onChange,
  ]);

  const getValidToYears = () => {
    if (!fromYear) return years;
    return years.filter((year) => parseInt(year.value) >= parseInt(fromYear));
  };

  const getValidToMonths = () => {
    if (!fromMonth || !fromYear || !toYear) return months;

    if (parseInt(toYear) === parseInt(fromYear)) {
      return months.filter(
        (month) => parseInt(month.value) >= parseInt(fromMonth)
      );
    }

    return months;
  };

  const switchPicker = () => {
    if (pickerType === 'bootstrap') {
      if (range.from && range.to) {
        const fromDate = new Date(range.from);
        const toDate = new Date(range.to);
        setFromMonth((fromDate.getMonth() + 1).toString());
        setFromYear(fromDate.getFullYear().toString());
        setToMonth((toDate.getMonth() + 1).toString());
        setToYear(toDate.getFullYear().toString());
      }
      setPickerType('dropdown');
    } else {
      setPickerType('bootstrap');
    }
  };

  function getDisplayDateLabel(range: {
    from: Date | string;
    to: Date | string;
    dynamic?: string;
  }) {
    if (range.dynamic === 'Custom Range' || !range.dynamic) {
      return `${DateForFilter(range.from)} - ${DateForFilter(range.to)}`;
    }
    if (range.dynamic) {
      const matchingDymamicRangeKey = (
        Object.keys(dynamicDateRanges) as Array<keyof typeof dynamicDateRanges>
      ).find((key) => dynamicDateRanges[key].label === range.dynamic);
      const matchingDynamicRange = matchingDymamicRangeKey
        ? dynamicDateRanges[matchingDymamicRangeKey]
        : undefined;
      if (matchingDynamicRange) {
        return matchingDynamicRange.label;
      }
    }
  }

  const onApply: EventHandler = (event, picker) => {
    let from = picker.startDate.toDate();
    let to = picker.endDate.toDate();
    setStorageRange && setStorageRange(picker.chosenLabel);
    onChange({
      from,
      to,
      dynamic: picker.chosenLabel || 'Custom Range',
    });
  };

  const onCancel: EventHandler = (event, picker) => {
    picker.setStartDate(new Date());
    picker.setEndDate(new Date());
    onChange({
      from: initialFromDate,
      to: initialToDate,
      dynamic: 'Custom Range',
    });
  };

  return (
    <div className={'daterangepicker-container ' + className}>
      <div>
        {pickerType === 'bootstrap' && (
          <DateRangePicker
            initialSettings={{
              showDropdowns: true,
              minYear: 2010,
              maxDate: new Date(currentYear + 1, 11, 31),
              buttonClasses: 'btn btn-default',
              autoUpdateInput: false,
              startDate: range.from,
              endDate: range.to,
              locale: {
                format: 'DD-MMM-YYYY',
                applyLabel: t('Apply'),
                cancelLabel: t('Clear'),
              },
              ...(useDynamicRanges && { ranges: dynamicRanges }),
            }}
            onApply={onApply}
            onCancel={onCancel}
          >
            <div>
              <Button
                icon={icons.CALENDAR}
                className="btn btn-default btn-outline"
                text={getDisplayDateLabel(range)}
              />
            </div>
          </DateRangePicker>
        )}

        {pickerType === 'dropdown' && (
          <div>
            <div className="d-flex align-items-center flex-nowrap daterange-height gap-4">
              <div className="d-flex align-items-center gap-4 daterange-height">
                <div className="daterange-select-year">
                  <FormSelect
                    name="fromMonth"
                    defaultValue={fromMonth}
                    onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                      setFromMonth(e.target.value)
                    }
                  >
                    <option value="">{t('Month')}</option>
                    {months.map((month) => (
                      <option key={month.value} value={month.value}>
                        {t(month.label)}
                      </option>
                    ))}
                  </FormSelect>
                </div>
                <div className="daterange-select-year">
                  <FormSelect
                    defaultValue={fromYear}
                    onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                      setFromYear(e.target.value)
                    }
                  >
                    <option value="">{t('Year')}</option>
                    {years.map((year) => (
                      <option key={year.value} value={year.value}>
                        {year.label}
                      </option>
                    ))}
                  </FormSelect>
                </div>
                <span
                  className="text-muted small px-2"
                  style={{ flex: '0 0 auto' }}
                >
                  {t('to')}
                </span>
              </div>
              <div className="d-flex align-items-center gap-4 daterange-height">
                <div className="daterange-select-month">
                  <FormSelect
                    name="toMonth"
                    defaultValue={toMonth}
                    onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                      setToMonth(e.target.value)
                    }
                  >
                    <option value="">{t('Month')}</option>
                    {getValidToMonths().map((month) => (
                      <option key={month.value} value={month.value}>
                        {t(month.label)}
                      </option>
                    ))}
                  </FormSelect>
                </div>
                <div className="daterange-select-year">
                  <FormSelect
                    name="toYear"
                    defaultValue={toYear}
                    onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                      setToYear(e.target.value)
                    }
                  >
                    <option value="">{t('Year')}</option>
                    {getValidToYears().map((year) => (
                      <option key={year.value} value={year.value}>
                        {year.label}
                      </option>
                    ))}
                  </FormSelect>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <Button
        className="btn btn-default btn-outline btn--square"
        icon={pickerType === 'bootstrap' ? icons.LIST : icons.CALENDAR}
        onClick={() => switchPicker()}
        title={
          pickerType === 'bootstrap'
            ? t('Switch to Month/Year Dropdowns')
            : t('Switch to Calendar Picker')
        }
      />
    </div>
  );
}

export default DateRange;
